<template>
    <div class="flex flex-col h-full px-2 pb-4">
        <div class="flex items-center my-2">
            <div class="w-2/3">
                <h1 class="text-xl font-bold mb-4">Eksporterte rapporter</h1>
                <UButton size="sm" icon="i-ph-download" @click="showSummaryReportExportsDialog">
                    Ny eksport...
                </UButton>
            </div>
            <div class="w-1/3 flex flex-col gap-2">
                <div class="w-full p-2 bg-base-100 mt-2 border rounded">
                    <div class="mb-2">Ny SAF-T eksport</div>
                    <div class="flex gap-4">
                        <date-range-selector v-model="dateRange"/>
                        <UButton
                            size="sm"
                            variant="outline"
                            color="neutral"
                            @click="createSaft"
                        >
                            Opprett
                        </UButton>
                    </div>
                </div>
            </div>
        </div>
        <loading-bar class="invisible" :class="{visible: loading}" data-cy="loading-indicator"/>
        <ag-grid-vue
            :column-defs="columns"
            :row-data="exports"
            :grid-options="gridOptions"
            class="w-full h-full"
            :theme="agTheme"
        />
    </div>
</template>

<script setup lang="ts">

import {computed, onMounted, onUnmounted, ref} from 'vue'
import {createSaftExport} from '@/services/saft.service'
import {ReportMeta} from '@/types/ReportMeta'
import {endOfYear, format, startOfYear} from 'date-fns'
import {AgGridVue} from 'ag-grid-vue3'
import ReportLinkCellRenderer from '@/components/ReportLinkCellRenderer.vue'
import {getReportMetas} from '@/services/infrastructure/storage.service'
import LoadingBar from '@/components/LoadingBar.vue'
import noRowsOverlayComponent from '@/components/NoRowsOverlayComponent.vue'
import {getSelectedOrgNo} from '@/tenants/org.service'
import {agTheme} from '@/agUtils/ag-theme.service'
import DateRangeSelector from '@/dateUtils/DateRangeSelector.vue'
import {getPersistedRangeOr} from '@/dateUtils/dateRange.service'
import {ColDef, GridOptions, ICellRendererParams} from 'ag-grid-community'
import {globalDefaultColDef} from '@/agUtils'
import {useEmitter} from '@/services/infrastructure/emitter'
import {REPORT_GENERATED} from '@/bank/bank.events'
import {showExportsDialog} from '@/exports/exports.service'
import {ReportType} from '@/types/reports/Report'
import {ORG_SELECTED} from '@/types/events'

const {onEvents, cleanUp} = useEmitter()

const exports = ref<ReportMeta[]>([])
const fromDate = ref<string>(formatDate(startOfYear(new Date())))
const toDate = ref<string>(formatDate(endOfYear(new Date())))
const loading = ref<boolean>(false)
import {v4 as uuidv4} from 'uuid'
const dateRange = computed({
    get: () => {
        return getPersistedRangeOr({
            start: new Date(fromDate.value),
            end: new Date(toDate.value)
        })
    },
    set: (dateRange) => {
        fromDate.value = format(dateRange.start, 'yyyy-MM-dd')
        toDate.value = format(dateRange.end, 'yyyy-MM-dd')
        emitChange()
    }
})

const emit = defineEmits(['update'])

const emitChange = () => {
    emit('update', fromDate.value, toDate.value)
}

function formatDate(date: Date) {
    return format(date, 'yyyy-MM-dd')
}

const createSaft = async () => {
    const orgNo = getSelectedOrgNo()
    if (orgNo) {
        await createSaftExport(orgNo, fromDate.value, toDate.value)
        exports.value = await getReportMetas()
    }
}

const showSummaryReportExportsDialog = () => {
    showExportsDialog(
        ReportType.BALREP,
        {
            fromDate: fromDate.value,
            toDate: toDate.value,
            project: '',
            department:''
        }
    )
}

const fetchReports = async () => {
    exports.value = await getReportMetas()
    exports.value.sort((a, b) => {
        const dateA = b.documentMeta.createdAt ? new Date(b.documentMeta.createdAt).getTime() : 0
        const dateB = a.documentMeta.createdAt ? new Date(a.documentMeta.createdAt).getTime() : 0
        return dateA - dateB
    })
}

onMounted(async () => {
    loading.value = true
    await fetchReports()
    setTimeout(() => {
        loading.value = false
    }, 100)
    onEvents([ORG_SELECTED, REPORT_GENERATED], () => fetchReports())
})

onUnmounted(cleanUp)

const gridOptions: GridOptions = {
    animateRows: true,
    noRowsOverlayComponent: noRowsOverlayComponent,
    noRowsOverlayComponentParams: {
        getMessage: () => (loading.value ? 'Laster....' : 'Ingen eksporterte rapporter'),
    },
    suppressDragLeaveHidesColumns: true,
    getRowId: (params: { data: ReportMeta }) => params.data.documentMeta.filename + uuidv4(),
    defaultColDef: {
        ...globalDefaultColDef,
        sortable: true,
        resizable: true,
    }
}

const formattedDate = (dateStr: string) => {
    return format(new Date(dateStr), 'dd.MM.yyyy')
}

const columns: ColDef[] = [
    {
        headerName: 'Rapporttype',
        field: 'reportType',
        width: 130
    },
    {
        headerName: 'Opprettet',
        field: 'documentMeta.createdAt',
        cellRenderer: (params: ICellRendererParams) => {
            return formattedDate(params.value)
        },
        width: 200
    },
    {
        headerName: 'Startdato',
        field: 'fromDate',
        cellRenderer: (params: ICellRendererParams) => {
            return formattedDate(params.value)
        },
        width: 130
    },
    {
        headerName: 'Sluttdato',
        field: 'toDate',
        cellRenderer: (params: ICellRendererParams) => {
            return formattedDate(params.value)
        },
        width: 130
    },
    {
        headerName: 'Last ned',
        cellRenderer: ReportLinkCellRenderer,
        flex: 1
    }
]
</script>

<style scoped>

</style>